import { Component, Inject, inject, signal, computed } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { BankAccountsStore } from 'src/app/store/bank-accounts.store';
import { AuthStore } from 'src/app/store/auth.store';
import { BankAccount, BankAccountStatus } from 'src/app/store/types';
import { WalletIconComponent } from '../icons/wallet-icon.component';
import { CheckCircleIconComponent } from '../icons/check-circle-icon.component';
import { DecimalsPipe } from 'src/app/pipes/decimals.pipe';

export interface AccountSelectionDialogData {
  title?: string;
  description?: string;
  accounts: BankAccount[];
  selectedAccountId?: string;
}

@Component({
  selector: 'app-account-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatListModule,
    MatTooltipModule,
    WalletIconComponent,
    CheckCircleIconComponent,
    DecimalsPipe
  ],
  template: `
    <div mat-dialog-title class="!pb-0">
      <div class="border-b border-gray-200 py-2">
        <h2 class="text-lg font-semibold text-gray-900">{{ title }}</h2>
        <p class="text-sm text-gray-600 mt-1">{{ description }}</p>
      </div>
    </div>
    <div mat-dialog-content class="sm:!p-0  !overflow-hidden">
      <div class="max-h-96 overflow-y-auto !p-2 sm:p-3 !pt-0">
        @if (accounts?.length) {
          <!-- Cuentas Propias -->
          @if (ownAccounts().length) {
            <div class="px-3 mt-3 pb-3">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Mis Cuentas
              </h3>
            </div>
            <mat-list class="sm:!px-3 !px-1 !pt-0">
              @for (account of ownAccounts(); track account.id) {
                <mat-list-item
                  [ngClass]="{
                    '!h-auto mb-2 !rounded-3xl !shadow-none !border-none transition-colors': true,
                    '!cursor-pointer hoverable': isAccountActive(account) || account.status === 'SUSPENDED',
                    '!cursor-not-allowed opacity-60': !isAccountActive(account) && account.status !== 'SUSPENDED',
                    'with-border bg-primary-50 border-l-4 border-blue-500': isSelected(account),
                    'bg-gray-50': !isAccountActive(account)
                  }"
                  (click)="selectAccount(account)"
                >
                  <div class="flex items-center justify-between w-full py-1">
                    <div class="flex items-center gap-3 flex-1">
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center"
                        [ngClass]="{
                          'bg-primary-100': isAccountActive(account),
                          'bg-yellow-100': !isAccountActive(account) && account.status === 'SUSPENDED',
                          'bg-gray-200': !isAccountActive(account) && account.status !== 'SUSPENDED'
                        }"
                      >
                        <app-wallet-icon
                          [width]="'26'"
                          [height]="'26'"
                          [ngClass]="{
                            'text-primary-600': isAccountActive(account),
                            'text-yellow-600': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        ></app-wallet-icon>
                      </div>
                      <div class="flex-1">
                        <div class="flex items-center gap-2">
                          <p
                            class="font-medium"
                            [ngClass]="{
                              'text-gray-900': isAccountActive(account),
                              'text-yellow-800': !isAccountActive(account) && account.status === 'SUSPENDED',
                              'text-gray-500': !isAccountActive(account) && account.status !== 'SUSPENDED'
                            }"
                          >
                            {{ account.accountNumber }}
                          </p>
                          @if (hasOriginMetadata(account)) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-help ml-auto"
                              [ngClass]="getOriginBadgeColor(account.metadata!.origin!)"
                              [matTooltip]="account.isLegacy && account.legacyId ? 'ID Legacy: ' + account.legacyId : ''"
                              matTooltipTouchGestures="on"
                              matTooltipShowDelay="0"
                              (click)="$event.stopPropagation()"
                            >
                              {{ account.metadata!.origin }}
                            </span>
                          }
                          @if (!isAccountActive(account)) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              [ngClass]="{
                                'bg-yellow-100 text-yellow-700': account.status === 'SUSPENDED',
                                'bg-red-100 text-red-700': account.status !== 'SUSPENDED'
                              }"
                            >
                              {{ getStatusLabel(account.status) }}
                            </span>
                          }
                        </div>
                        <p
                          class="text-sm"
                          [ngClass]="{
                            'text-gray-600': isAccountActive(account),
                            'text-yellow-700': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        >
                          {{ account.currency.code }} -
                          {{ account.description || getAccountDefaultDescription(account) }}
                        </p>
                        <p
                          class="text-xs"
                          [ngClass]="{
                            'text-gray-500': isAccountActive(account),
                            'text-yellow-600': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        >
                          Balance: {{ account.currency.symbol
                          }}{{ account.availableBalance | decimals }}
                        </p>
                      </div>
                    </div>

                    @if (isSelected(account)) {
                      <app-check-circle-icon
                        [width]="'24'"
                        [height]="'24'"
                        class="text-primary-600"
                      ></app-check-circle-icon>
                    }
                  </div>
                </mat-list-item>
              }
            </mat-list>
          }

          <!-- Cuentas Autorizadas -->
          @if (authorizedAccounts().length) {
            <div class="px-3 pt-3 pb-2 border-t border-gray-200">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Cuentas Autorizadas
              </h3>
            </div>
            <mat-list class="sm:!px-3 !px-1 !pt-0">
              @for (account of authorizedAccounts(); track account.id) {
                <mat-list-item
                  [ngClass]="{
                    '!h-auto mb-2 !rounded-3xl !shadow-none !border-none transition-colors': true,
                    '!cursor-pointer hoverable': isAccountActive(account) || account.status === 'SUSPENDED',
                    '!cursor-not-allowed opacity-60': !isAccountActive(account) && account.status !== 'SUSPENDED',
                    'with-border bg-violet-50 border-l-4 border-violet-500': isSelected(account),
                    'bg-gray-50': !isAccountActive(account)
                  }"
                  (click)="selectAccount(account)"
                >
                  <div class="flex items-center justify-between w-full py-1">
                    <div class="flex items-center gap-3 flex-1">
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center"
                        [ngClass]="{
                          'bg-violet-100': isAccountActive(account),
                          'bg-yellow-100': !isAccountActive(account) && account.status === 'SUSPENDED',
                          'bg-gray-200': !isAccountActive(account) && account.status !== 'SUSPENDED'
                        }"
                      >
                        <app-wallet-icon
                          [width]="'26'"
                          [height]="'26'"
                          [ngClass]="{
                            'text-violet-600': isAccountActive(account),
                            'text-yellow-600': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        ></app-wallet-icon>
                      </div>
                      <div class="flex-1">
                        <div class="flex items-center gap-1 flex-wrap">
                          <p
                            class="font-medium"
                            [ngClass]="{
                              'text-gray-900': isAccountActive(account),
                              'text-yellow-800': !isAccountActive(account) && account.status === 'SUSPENDED',
                              'text-gray-500': !isAccountActive(account) && account.status !== 'SUSPENDED'
                            }"
                          >
                            {{ account.accountNumber }}
                          </p>
                          @if (hasOriginMetadata(account)) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-help ml-auto"
                              [ngClass]="getOriginBadgeColor(account.metadata!.origin!)"
                              [matTooltip]="account.isLegacy && account.legacyId ? 'ID Legacy: ' + account.legacyId : ''"
                              matTooltipTouchGestures="on"
                              matTooltipShowDelay="0"
                              (click)="$event.stopPropagation()"
                            >
                              {{ account.metadata!.origin }}
                            </span>
                          }
                          @if (
                            account.customer?.business?.businessName || account.customer?.person
                          ) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700"
                            >
                              {{
                                account.customer?.business?.businessName ||
                                  account.customer?.person?.firstName +
                                    ' ' +
                                    account.customer?.person?.lastName
                              }}
                            </span>
                          }
                          @if (!isAccountActive(account)) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              [ngClass]="{
                                'bg-yellow-100 text-yellow-700': account.status === 'SUSPENDED',
                                'bg-red-100 text-red-700': account.status !== 'SUSPENDED'
                              }"
                            >
                              {{ getStatusLabel(account.status) }}
                            </span>
                          }
                        </div>
                        <p
                          class="text-sm"
                          [ngClass]="{
                            'text-gray-600': isAccountActive(account),
                            'text-yellow-700': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        >
                          {{ account.currency.code }} -
                          {{
                            account.customer?.customerType === 'BUSINESS'
                              ? 'Cuenta de Empresa'
                              : 'Cuenta Personal'
                          }}
                        </p>
                        <p
                          class="text-xs"
                          [ngClass]="{
                            'text-gray-500': isAccountActive(account),
                            'text-yellow-600': !isAccountActive(account) && account.status === 'SUSPENDED',
                            'text-gray-400': !isAccountActive(account) && account.status !== 'SUSPENDED'
                          }"
                        >
                          Balance: {{ account.currency.symbol
                          }}{{ account.availableBalance | decimals }}
                        </p>
                      </div>
                    </div>

                    @if (isSelected(account)) {
                      <app-check-circle-icon
                        [width]="'24'"
                        [height]="'24'"
                        class="text-violet-600"
                      ></app-check-circle-icon>
                    }
                  </div>
                </mat-list-item>
              }
            </mat-list>
          }
        } @else {
          <div class="p-8 text-center">
            <app-wallet-icon
              [width]="'64'"
              [height]="'64'"
              class="text-gray-400 mx-auto mb-4"
            ></app-wallet-icon>
            <p class="text-gray-600">No hay cuentas bancarias disponibles</p>
          </div>
        }
      </div>

      <div class="sm:p-4 p-3 pt-4 border-t border-gray-200 flex justify-end gap-2">
        <button mat-button (click)="onCancel()" class="text-gray-600">Cancelar</button>
        <button
          mat-flat-button
          color="primary"
          (click)="onConfirm()"
          [disabled]="!tempSelectedAccountId()"
        >
          Confirmar
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      ::ng-deep .mat-mdc-dialog-content {
        padding: 0 !important;
        margin: 0 !important;
      }
      ::ng-deep .mat-mdc-dialog-title::before {
        display: none;
      }

      ::ng-deep .mat-mdc-list-item {
        border-bottom: 1px solid #f3f4f6;
      }

      ::ng-deep .mat-mdc-list-item:last-child {
        border-bottom: none;
      }

      ::ng-deep .mat-mdc-list-item.hoverable:hover {
        background-color: #f9fafb;
      }

      ::ng-deep .mat-mdc-list-item.opacity-60 {
        pointer-events: auto;
      }
    `
  ]
})
export class AccountSelectionDialogComponent {
  bankAccountsStore = inject(BankAccountsStore);
  authStore = inject(AuthStore);
  private dialogRef = inject(MatDialogRef<AccountSelectionDialogComponent>);

  // Propiedades del componente
  title: string;
  description: string;
  accounts: BankAccount[];

  // Selección temporal
  tempSelectedAccountId = signal<string | undefined>(undefined);

  // Computed signals para separar cuentas propias de autorizadas
  ownAccounts = computed(() => this.accounts.filter((acc) => acc.ownAccount !== false));

  authorizedAccounts = computed(() =>
    this.accounts.filter((acc) => acc.ownAccount === false && acc.authorizedAccount === true)
  );

  constructor(@Inject(MAT_DIALOG_DATA) public data: AccountSelectionDialogData | null) {
    this.title = data?.title || 'Seleccionar Cuenta Bancaria';
    this.description = data?.description || 'Elige la cuenta bancaria que deseas utilizar';
    this.accounts = data?.accounts || [];
    this.tempSelectedAccountId.set(this.getInitialSelection());
  }

  private isAccountSelectable(account: BankAccount): boolean {
    return account?.status === BankAccountStatus.ACTIVE || 
           account?.status === BankAccountStatus.SUSPENDED;
  }

  private getInitialSelection(): string | undefined {
    // Filtrar cuentas seleccionables (activas y suspendidas) para la selección inicial
    const selectableAccounts = this.accounts.filter((acc) =>
      this.isAccountSelectable(acc)
    );

    // Si se proporciona una cuenta seleccionada, verificar que sea seleccionable
    if (this.data?.selectedAccountId) {
      const selectedAccount = this.accounts.find(
        (acc) => acc.accountNumber === this.data.selectedAccountId
      );
      if (selectedAccount && this.isAccountSelectable(selectedAccount)) {
        return this.data.selectedAccountId;
      }
    }

    // Si hay una sola cuenta seleccionable disponible, seleccionarla automáticamente
    if (selectableAccounts.length === 1) {
      return selectableAccounts[0].accountNumber;
    }

    return undefined;
  }

  isSelected(account: BankAccount): boolean {
    return account.accountNumber === this.tempSelectedAccountId();
  }

  isAccountActive(account: BankAccount): boolean {
    return this.bankAccountsStore.isAccountActive(account);
  }

  selectAccount(account: BankAccount) {
    // Permitir seleccionar cuentas activas y suspendidas (para ver información histórica)
    // No permitir seleccionar cuentas bloqueadas, cerradas, congeladas o deshabilitadas
    const canSelect = account.status === BankAccountStatus.ACTIVE || 
                     account.status === BankAccountStatus.SUSPENDED;
    
    if (!canSelect) {
      return;
    }
    this.tempSelectedAccountId.set(account.accountNumber);
  }

  getStatusLabel(status: BankAccountStatus): string {
    switch (status) {
      case BankAccountStatus.ACTIVE:
        return 'Activa';
      case BankAccountStatus.INACTIVE:
        return 'Inactiva';
      case BankAccountStatus.SUSPENDED:
        return 'Suspendida';
      case BankAccountStatus.BLOCKED:
        return 'Bloqueada';
      case BankAccountStatus.PENDING:
        return 'Pendiente';
      case BankAccountStatus.CLOSED:
        return 'Cerrada';
      case BankAccountStatus.DISABLED:
        return 'Deshabilitada';
      case BankAccountStatus.FROZEN:
        return 'Congelada';
      default:
        return status;
    }
  }

  getPermissionLabel(permission: string): string {
    switch (permission) {
      case 'full_access':
        return 'Acceso Completo';
      case 'read_only':
        return 'Solo Lectura';
      case 'limited':
        return 'Limitado';
      default:
        return permission;
    }
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onConfirm() {
    const selectedAccountNumber = this.tempSelectedAccountId();
    const selectedAccount = this.accounts.find(
      (account) => account.accountNumber === selectedAccountNumber
    );
    this.dialogRef.close(selectedAccount);
  }

  getAccountDefaultDescription(account: BankAccount) {
    return account.accountType.name;
  }

  getOriginBadgeColor(origin: 'Ecotreew' | 'Provtreew'): string {
    switch (origin) {
      case 'Ecotreew':
        return 'bg-green-100 text-green-700';
      case 'Provtreew':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  hasOriginMetadata(account: BankAccount): boolean {
    return !!(account.metadata?.origin);
  }
}
